import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * The grammar every result view shares (26/10/2026, direction « A + C »):
 * three text levels, one bar, one label/value row and the decision band.
 * Spacing steps: 12px inside a row, 24px between blocks, 48px between results.
 */
export const VIEW_TEXT = {
  title: 'text-lg font-semibold leading-6 text-domino-ink',
  lead: 'text-[17px] leading-[26px] text-domino-ink',
  body: 'text-[15px] leading-[22px] text-domino-ink',
  detail: 'text-[13px] leading-[18px] text-domino-inkSoft',
} as const;

const clampPercent = (value: number): number => Math.min(100, Math.max(0, value));

/** The only bar of a result: plum on a hairline track. */
export const MeterBar = ({ value, max, className }: { value: number; max: number; className?: string }) => (
  <div className={cn('h-1.5 rounded-full bg-domino-border', className)} aria-hidden="true">
    <div className="h-1.5 rounded-full bg-domino-aubergine" style={{ width: `${max > 0 ? clampPercent((value / max) * 100) : 0}%` }} />
  </div>
);

export type RowTone = 'good' | 'warning' | 'critical' | 'neutral';

/** Known: filled dot; to confirm: amber; missing: hollow ring (never red). */
const ROW_DOT: Record<RowTone, string> = {
  good: 'bg-[var(--domino-good)]',
  warning: 'bg-[var(--domino-warning)]',
  critical: 'border-[1.5px] border-domino-inkSoft',
  neutral: 'bg-domino-inkSoft',
};

export interface LabelValueRow {
  label: string;
  value: ReactNode;
  tone?: RowTone;
}

/** Label in a fixed column, value left-aligned after it, rows split by hairlines; label above value when narrow. */
export const LabelValueList = ({ rows, columns = 1, className }: { rows: LabelValueRow[]; columns?: 1 | 2; className?: string }) => (
  <dl className={cn('grid gap-x-10 border-t border-domino-border', columns === 2 && 'cq-md:grid-cols-2', className)}>
    {rows.map((row, index) => (
      <div key={`${row.label}-${index}`} className="grid min-w-0 grid-cols-1 gap-1 border-b border-domino-border py-3 cq-sm:grid-cols-[clamp(96px,32%,148px)_minmax(0,1fr)] cq-sm:items-baseline cq-sm:gap-3">
        <dt className={cn('flex min-w-0 items-baseline gap-2', VIEW_TEXT.detail)}>
          {row.tone && <span className={cn('h-1.5 w-1.5 shrink-0 self-center rounded-full', ROW_DOT[row.tone])} aria-hidden="true" />}
          <span className="truncate" title={row.label}>{row.label}</span>
        </dt>
        <dd className={cn('min-w-0 break-words', VIEW_TEXT.body, row.tone === 'critical' && 'text-domino-inkSoft')}>{row.value}</dd>
      </div>
    ))}
  </dl>
);

interface DecisionBandProps {
  /** Short state on a sun pill: « 3/6 · À vérifier ». */
  badge?: string | null;
  /** What the result is about: « Qualification MEDDIC · Talus Logiciels ». */
  context?: string | null;
  /** The decision itself, one sentence. */
  children: ReactNode;
  /** One action, drawn light on the plum band. */
  action?: ReactNode;
}

/**
 * The head of a result that leads to a decision: state, the sentence and its
 * one action on a plum band. Light on plum, never a yellow or orange button.
 */
export const DecisionBand = ({ badge, context, children, action }: DecisionBandProps) => (
  <div className="flex flex-col gap-4 rounded-xl bg-domino-aubergine px-6 py-5 text-white cq-md:flex-row cq-md:items-center cq-md:gap-8">
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      {(badge || context) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] leading-[18px]">
          {badge && <span className="rounded-full bg-domino-sun px-2.5 py-0.5 font-semibold text-domino-ink tabular-nums">{badge}</span>}
          {context && <span className="text-domino-plumTint">{context}</span>}
        </div>
      )}
      <div className="text-[19px] font-medium leading-7">{children}</div>
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);

/** Classes that turn a ViewActionButton light on the plum band. */
export const ON_BAND_ACTION_CLASS =
  'bg-domino-warm text-domino-aubergine shadow-none hover:bg-white hover:text-domino-aubergine focus:ring-domino-sun focus:ring-offset-domino-aubergine';
